// =============================================================================
// src/features/auth/auth-session.ts — Sessão mock do operador (persistida).
//
// Sistema de login do mockup (2026-07): NÃO há backend de auth — a "sessão" é um
//   envelope versionado em localStorage que o gate de main.ts consulta antes do
//   bootstrap. Storage é INJETADO (DI) para testes determinísticos, espelhando o
//   padrão de src/views/workspace/workspace-store.ts (try/catch best-effort).
//
// SRP: só leitura/escrita/validação da sessão. Sem DOM, sem markup, sem chrome.
// =============================================================================

/** Provedor usado no acesso. Desde 2026-07-20 SÓ o formulário cria sessão
 *  ('email'); 'google'/'govbr' seguem no union para LER sessões persistidas
 *  antes da mudança (os botões sociais não autenticam mais). */
export type AuthProvider = 'email' | 'google' | 'govbr';

/** Sessão persistida do operador logado (mock; vira token real quando houver backend). */
export interface AuthOperatorSession {
  readonly operatorEmail: string;
  readonly provider: AuthProvider;
}

/** Subset de Storage que a sessão usa (DI; window.localStorage em produção). */
export type AuthSessionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Chave única no localStorage (greppável; a versão vive no payload, não na chave). */
export const AUTH_SESSION_STORAGE_KEY = 'mapaPoliticoAuthSession';

/** Versão do schema persistido; mismatch invalida a sessão (força novo login). */
const AUTH_SESSION_SCHEMA_VERSION = 1;

/** Provedores aceitos pelo type guard (fonte única, alinhada a AuthProvider). */
const AUTH_PROVIDERS: readonly AuthProvider[] = ['email', 'google', 'govbr'];

/** Envelope persistido: versão + sessão (padrão do workspace-store). */
interface AuthPersistedSession {
  readonly version: number;
  readonly session: AuthOperatorSession;
}

/** Type guard do envelope — barra JSON estranho, null, array e versão errada. */
function isAuthPersistedSession(value: unknown): value is AuthPersistedSession {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { version?: unknown; session?: unknown };
  return (
    candidate.version === AUTH_SESSION_SCHEMA_VERSION &&
    isAuthOperatorSession(candidate.session)
  );
}

/** Type guard da sessão: e-mail string não vazia + provider conhecido. */
function isAuthOperatorSession(value: unknown): value is AuthOperatorSession {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { operatorEmail?: unknown; provider?: unknown };
  return (
    typeof candidate.operatorEmail === 'string' &&
    candidate.operatorEmail.length > 0 &&
    (AUTH_PROVIDERS as readonly unknown[]).includes(candidate.provider)
  );
}

/**
 * Lê e valida a sessão persistida; null quando ausente/inválida/storage indisponível.
 *
 * @example readAuthSession() // { operatorEmail: 'nome@senado.leg.br', provider: 'email' } | null
 */
export function readAuthSession(
  storage: AuthSessionStorage = window.localStorage,
): AuthOperatorSession | null {
  try {
    const raw = storage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isAuthPersistedSession(parsed) ? parsed.session : null;
  } catch {
    return null;
  }
}

/** True quando há sessão válida — o gate de main.ts decide login vs. bootstrap. */
export function hasAuthSession(storage: AuthSessionStorage = window.localStorage): boolean {
  return readAuthSession(storage) !== null;
}

/**
 * Persiste a sessão (best-effort: storage cheio/indisponível não derruba o login).
 *
 * @example persistAuthSession({ operatorEmail: 'nome@senado.leg.br', provider: 'email' })
 */
export function persistAuthSession(
  session: AuthOperatorSession,
  storage: AuthSessionStorage = window.localStorage,
): void {
  const envelope: AuthPersistedSession = { version: AUTH_SESSION_SCHEMA_VERSION, session };
  try {
    storage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // best-effort: sem storage a sessão vive só até o reload (aceitável no mock).
  }
}

/** Remove a sessão persistida (ação "Sair" da topbar). Best-effort como o persist. */
export function clearAuthSession(storage: AuthSessionStorage = window.localStorage): void {
  try {
    storage.removeItem(AUTH_SESSION_STORAGE_KEY);
  } catch {
    // best-effort: storage indisponível já significa que não há sessão a remover.
  }
}
